import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { refDebounced } from '@vueuse/core'
import Api from 'fou-api'
import { Toaster } from 'src/utils/helper/toaster'
import i18n from 'src/assets/i18n/i18n'

const DISMISS_STORAGE_KEY = 'inactiveFamiliesBannerDismissed'
const dismissed = ref(localStorage.getItem(DISMISS_STORAGE_KEY) === 'true')

export function useInactiveFamiliesDismiss () {
  function dismiss () {
    dismissed.value = true
    localStorage.setItem(DISMISS_STORAGE_KEY, 'true')
  }

  return { dismissed, dismiss }
}

const submittingFoundries = ref(new Set())

function updateFoundryReleasedCountInLS (foundryId, newCount) {
  const foundries = JSON.parse(window.localStorage.getItem('userFoundries') || '[]')
  const foundry = foundries.find(f => f.id === foundryId)
  if (foundry) {
    foundry.previouslyReleasedFamiliesCount = newCount
    window.localStorage.setItem('userFoundries', JSON.stringify(foundries))
  }
}

export function useReReleaseSubmit () {
  const { t } = i18n().global
  const store = useStore()

  const isSubmitting = computed(() => submittingFoundries.value.size > 0)

  function isFoundrySubmitting (foundryId) {
    return submittingFoundries.value.has(foundryId)
  }

  function updateFoundryReleasedCount (foundryId, newCount) {
    updateFoundryReleasedCountInLS(foundryId, newCount)
    store.commit('profile/updateFoundryReleasedCount', { foundryId, count: newCount })
  }

  async function submitReRelease ({ foundryId, familyIds, all }) {
    if (submittingFoundries.value.has(foundryId)) return false
    const next = new Set(submittingFoundries.value)
    next.add(foundryId)
    submittingFoundries.value = next
    try {
      await Api.Families.reReleaseFamilies({
        foundryId,
        ...(all ? { all: true } : { familyIds })
      })
      return true
    } catch (error) {
      if (error?.response?.status === 400) {
        Toaster.notifyError(t('pages.reactivate_families.re_release_error'))
      }
      return false
    } finally {
      const next = new Set(submittingFoundries.value)
      next.delete(foundryId)
      submittingFoundries.value = next
    }
  }

  return { isSubmitting, isFoundrySubmitting, submitReRelease, updateFoundryReleasedCount }
}

const PAGE_SIZE = 40

const TAB_ALL = 'all'
const TAB_SELECTED = 'selected'
const TAB_UNSELECTED = 'unselected'

const SORT_ALPHA_ASC = 'alpha-asc'
const SORT_ALPHA_DESC = 'alpha-desc'
const SORT_MOST_STYLES = 'most-styles'
const SORT_LEAST_STYLES = 'least-styles'

export const SORT_OPTIONS = [
  { value: SORT_ALPHA_ASC, labelKey: 'pages.reactivate_families.sort_alpha_asc' },
  { value: SORT_ALPHA_DESC, labelKey: 'pages.reactivate_families.sort_alpha_desc' },
  { value: SORT_MOST_STYLES, labelKey: 'pages.reactivate_families.sort_most_styles' },
  { value: SORT_LEAST_STYLES, labelKey: 'pages.reactivate_families.sort_least_styles' }
]

export const TABS = [TAB_ALL, TAB_SELECTED, TAB_UNSELECTED]

export { TAB_ALL, TAB_SELECTED, TAB_UNSELECTED, PAGE_SIZE }

export default function useReactivateFamilies () {
  const router = useRouter()
  const { t } = i18n().global

  const allFamilies = ref([])
  const selectedFamilyIds = ref(new Set())
  const activeTab = ref(TAB_ALL)
  const searchQuery = ref('')
  const debouncedSearch = refDebounced(searchQuery, 300)
  const sortKey = ref(SORT_ALPHA_ASC)
  const currentPage = ref(1)
  const isLoading = ref(false)

  const pendingRemovalIds = ref(new Set())
  const removalTimers = new Map()

  const totalCount = computed(() => allFamilies.value.length)
  const selectedCount = computed(() => selectedFamilyIds.value.size)
  const unselectedCount = computed(() => totalCount.value - selectedCount.value)

  const searchFilteredTabCounts = computed(() => {
    const q = debouncedSearch.value.trim().toLowerCase()
    if (!q) return { all: totalCount.value, selected: selectedCount.value, unselected: unselectedCount.value }
    const ids = selectedFamilyIds.value
    let all = 0; let sel = 0; let unsel = 0
    for (const f of allFamilies.value) {
      if (f.familyName.toLowerCase().includes(q)) {
        all++
        if (ids.has(f.familyId)) sel++; else unsel++
      }
    }
    return { all, selected: sel, unselected: unsel }
  })

  const filteredByTab = computed(() => {
    const ids = selectedFamilyIds.value
    const pending = pendingRemovalIds.value
    if (activeTab.value === TAB_SELECTED) {
      return allFamilies.value.filter(f => ids.has(f.familyId) || pending.has(f.familyId))
    }
    if (activeTab.value === TAB_UNSELECTED) {
      return allFamilies.value.filter(f => !ids.has(f.familyId) || pending.has(f.familyId))
    }
    return allFamilies.value
  })

  const filteredBySearch = computed(() => {
    const q = debouncedSearch.value.trim().toLowerCase()
    if (!q) return filteredByTab.value
    return filteredByTab.value.filter(f => f.familyName.toLowerCase().includes(q))
  })

  const sortedFamilies = computed(() => {
    const list = [...filteredBySearch.value]
    switch (sortKey.value) {
      case SORT_ALPHA_ASC:
        return list.sort((a, b) => a.familyName.localeCompare(b.familyName))
      case SORT_ALPHA_DESC:
        return list.sort((a, b) => b.familyName.localeCompare(a.familyName))
      case SORT_MOST_STYLES:
        return list.sort((a, b) => b.styleCount - a.styleCount || a.familyName.localeCompare(b.familyName))
      case SORT_LEAST_STYLES:
        return list.sort((a, b) => a.styleCount - b.styleCount || a.familyName.localeCompare(b.familyName))
      default:
        return list
    }
  })

  const totalPages = computed(() => Math.ceil(sortedFamilies.value.length / PAGE_SIZE))

  const paginatedFamilies = computed(() => {
    const start = (currentPage.value - 1) * PAGE_SIZE
    return sortedFamilies.value.slice(start, start + PAGE_SIZE)
  })

  const filteredCount = computed(() => sortedFamilies.value.length)

  const isAllSelectedInView = computed(() => {
    if (sortedFamilies.value.length === 0) return false
    return sortedFamilies.value.every(f => selectedFamilyIds.value.has(f.familyId))
  })

  const isSelectAllDisabled = computed(() => {
    if (activeTab.value === TAB_SELECTED) return true
    return sortedFamilies.value.length === 0
  })

  const hasSelections = computed(() => selectedFamilyIds.value.size > 0)

  const isEmptyState = computed(() => {
    if (isLoading.value) return false
    return sortedFamilies.value.length === 0 && allFamilies.value.length > 0
  })

  const emptyStateType = computed(() => {
    if (debouncedSearch.value.trim()) return 'no-results'
    if (activeTab.value === TAB_SELECTED && selectedCount.value === 0) return 'no-selected'
    if (activeTab.value === TAB_UNSELECTED && unselectedCount.value === 0) return 'all-selected'
    return 'no-results'
  })

  watch([activeTab, debouncedSearch, sortKey], () => {
    currentPage.value = 1
  })

  watch(totalPages, (pages) => {
    if (currentPage.value > pages && pages > 0) {
      currentPage.value = pages
    }
  })

  function isSelected (familyId) {
    return selectedFamilyIds.value.has(familyId)
  }

  function toggleFamily (familyId) {
    if (pendingRemovalIds.value.has(familyId)) {
      clearTimeout(removalTimers.get(familyId))
      removalTimers.delete(familyId)
      const nextPending = new Set(pendingRemovalIds.value)
      nextPending.delete(familyId)
      pendingRemovalIds.value = nextPending
    }

    const next = new Set(selectedFamilyIds.value)
    if (next.has(familyId)) {
      next.delete(familyId)
    } else {
      next.add(familyId)
    }
    selectedFamilyIds.value = next

    if (activeTab.value === TAB_SELECTED || activeTab.value === TAB_UNSELECTED) {
      const nextPending = new Set(pendingRemovalIds.value)
      nextPending.add(familyId)
      pendingRemovalIds.value = nextPending
      const timerId = setTimeout(() => {
        const p = new Set(pendingRemovalIds.value)
        p.delete(familyId)
        pendingRemovalIds.value = p
        removalTimers.delete(familyId)
      }, 500)
      removalTimers.set(familyId, timerId)
    }
  }

  function clearAllPendingTimers () {
    removalTimers.forEach(timerId => clearTimeout(timerId))
    removalTimers.clear()
    pendingRemovalIds.value = new Set()
  }

  function selectAll () {
    clearAllPendingTimers()
    const next = new Set(selectedFamilyIds.value)
    sortedFamilies.value.forEach(f => next.add(f.familyId))
    selectedFamilyIds.value = next
  }

  function resetSelection () {
    clearAllPendingTimers()
    selectedFamilyIds.value = new Set()
  }

  function setTab (tab) {
    clearAllPendingTimers()
    activeTab.value = tab
  }

  function setSort (key) {
    sortKey.value = key
  }

  function setPage (page) {
    currentPage.value = page
  }

  async function fetchFamilies (foundryId) {
    isLoading.value = true
    try {
      const response = await Api.Families.getDeReleasedFamilies(foundryId)
      const families = response.data?.families

      if (!families || families.length === 0) {
        Toaster.notifyError(t('pages.reactivate_families.no_families_toast'))
        router.replace({ name: 'home' })
        return
      }

      allFamilies.value = families
    } catch (error) {
      if (error?.response?.status === 404) {
        Toaster.notifyError(t('pages.reactivate_families.no_families_toast'))
        router.replace({ name: 'home' })
      }
    } finally {
      isLoading.value = false
    }
  }

  return {
    allFamilies,
    selectedFamilyIds,
    activeTab,
    searchQuery,
    debouncedSearch,
    sortKey,
    currentPage,
    isLoading,

    totalCount,
    selectedCount,
    unselectedCount,
    filteredCount,
    searchFilteredTabCounts,
    sortedFamilies,
    paginatedFamilies,
    totalPages,
    isAllSelectedInView,
    isSelectAllDisabled,
    hasSelections,
    isEmptyState,
    emptyStateType,

    isSelected,
    toggleFamily,
    selectAll,
    resetSelection,
    clearAllPendingTimers,
    setTab,
    setSort,
    setPage,
    fetchFamilies,

    PAGE_SIZE,
    SORT_OPTIONS,
    TAB_ALL,
    TAB_SELECTED,
    TAB_UNSELECTED
  }
}
